(function (root, factory) {
  var engine = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = engine;
  }
  root.DebtPayoffEngine = engine;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  var EPSILON = 0.01;
  var MAX_MONTHS = 600;

  // Coerce arbitrary input to a finite, non-negative number.
  function safeNumber(value) {
    var n = typeof value === 'number' ? value : parseFloat(value);
    if (!isFinite(n) || n < 0) {
      return 0;
    }
    return n;
  }

  // Order the still-active debts by strategy priority.
  // Avalanche: highest APR first. Snowball: smallest balance first.
  // Ties are broken by original input order (stable) to keep results deterministic.
  function orderTargets(activeDebts, strategy) {
    var ordered = activeDebts.slice();
    if (strategy === 'avalanche') {
      ordered.sort(function (a, b) {
        if (b.rate !== a.rate) {
          return b.rate - a.rate;
        }
        return a.order - b.order;
      });
    } else if (strategy === 'snowball') {
      ordered.sort(function (a, b) {
        if (a.currentBalance !== b.currentBalance) {
          return a.currentBalance - b.currentBalance;
        }
        return a.order - b.order;
      });
    }
    return ordered;
  }

  function calculatePayoffDetailed(debts, extraPayment, strategy) {
    if (!debts || debts.length === 0) {
      return {
        months: 0,
        totalInterest: 0,
        debtPayoffs: [],
        monthlyData: [],
        paidOff: true,
        remainingBalance: 0
      };
    }

    var debtsCopy = debts.map(function (d, index) {
      return {
        name: d.name,
        balance: safeNumber(d.balance),
        rate: safeNumber(d.rate),
        minPayment: safeNumber(d.minPayment),
        currentBalance: safeNumber(d.balance),
        paidOffMonth: null,
        order: index
      };
    });

    var isMinimum = strategy === 'minimum';

    // Standard methodology keeps the total monthly budget constant: the sum of
    // every debt's ORIGINAL minimum payment (freed minimums stay in the pool as
    // debts are cleared) plus the user's extra payment. The `minimum` baseline
    // deliberately does NOT roll over: each debt only ever pays its own minimum.
    var baseMinimums = debtsCopy.reduce(function (sum, d) {
      return sum + d.minPayment;
    }, 0);
    var extraMoney = isMinimum ? 0 : safeNumber(extraPayment);
    var monthlyBudget = baseMinimums + extraMoney;

    var totalInterest = 0;
    var month = 0;
    var monthlyData = [];

    while (debtsCopy.some(function (d) { return d.currentBalance > EPSILON; }) && month < MAX_MONTHS) {
      month++;

      // 1. Accrue interest on every active debt.
      debtsCopy.forEach(function (debt) {
        if (debt.currentBalance > 0) {
          var monthlyInterest = debt.currentBalance * (debt.rate / 100 / 12);
          debt.currentBalance += monthlyInterest;
          totalInterest += monthlyInterest;
        }
      });

      if (isMinimum) {
        // Baseline: each active debt pays only its own minimum. No rollover,
        // no extra, no cascade. This preserves the historical baseline numbers.
        debtsCopy.forEach(function (debt) {
          if (debt.currentBalance > 0) {
            var payment = Math.min(debt.minPayment, debt.currentBalance);
            debt.currentBalance -= payment;
          }
        });
      } else {
        // 2. Apply each active debt's minimum (capped at its balance). Whatever
        //    of the fixed monthly budget is not consumed by minimums — freed
        //    minimums from paid-off debts, min-vs-balance remainders, and the
        //    extra payment — becomes the pool that accelerates the strategy target.
        var budgetRemaining = monthlyBudget;
        debtsCopy.forEach(function (debt) {
          if (debt.currentBalance > 0) {
            var payment = Math.min(debt.minPayment, debt.currentBalance);
            debt.currentBalance -= payment;
            budgetRemaining -= payment;
          }
        });

        if (budgetRemaining < 0) {
          budgetRemaining = 0;
        }

        // 3. Waterfall the remaining budget through the strategy-ordered debts.
        //    When a target clears, its leftover cascades to the next debt in the
        //    same month, repeating until the budget runs out or all debts clear.
        var active = debtsCopy.filter(function (d) { return d.currentBalance > EPSILON; });
        var ordered = orderTargets(active, strategy);
        for (var i = 0; i < ordered.length && budgetRemaining > EPSILON; i++) {
          var target = ordered[i];
          var applied = Math.min(budgetRemaining, target.currentBalance);
          target.currentBalance -= applied;
          budgetRemaining -= applied;
        }
      }

      // Record payoff month for any debt that just reached zero.
      debtsCopy.forEach(function (debt) {
        if (debt.currentBalance <= EPSILON && debt.paidOffMonth === null) {
          debt.paidOffMonth = month;
        }
      });

      var totalBalance = debtsCopy.reduce(function (sum, d) {
        return sum + Math.max(0, d.currentBalance);
      }, 0);

      monthlyData.push({ month: month, totalBalance: totalBalance });
    }

    var remainingBalance = debtsCopy.reduce(function (sum, d) {
      return sum + Math.max(0, d.currentBalance);
    }, 0);
    var paidOff = remainingBalance <= EPSILON;
    if (paidOff) {
      remainingBalance = 0;
    }

    return {
      months: month,
      totalInterest: totalInterest,
      paidOff: paidOff,
      remainingBalance: remainingBalance,
      debtPayoffs: debtsCopy.map(function (d) {
        // Never-paid debts report null rather than a fake cap month.
        return { name: d.name, month: d.paidOffMonth, paidOffMonth: d.paidOffMonth };
      }),
      monthlyData: monthlyData
    };
  }

  function calculate(input) {
    return {
      minimum: calculatePayoffDetailed(input.debts, input.extraPayment, 'minimum'),
      avalanche: calculatePayoffDetailed(input.debts, input.extraPayment, 'avalanche'),
      snowball: calculatePayoffDetailed(input.debts, input.extraPayment, 'snowball')
    };
  }

  return {
    calculate: calculate,
    calculatePayoffDetailed: calculatePayoffDetailed
  };
});
